// External libraries
import { Component } from "react";
import { v4 as uuidv4 } from "uuid";

// FontAwesome icons
import { faTrashCan } from "@fortawesome/pro-duotone-svg-icons";

// Preact Signals
import { effect } from "@preact/signals-react";

// Kendo React components
import { Button } from "@progress/kendo-react-buttons";
import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import { Label } from "@progress/kendo-react-labels";

// Internal components
import { ColorChangingIcon } from "../../Common/UI/ColorChangingIcon";
import { If } from "../../Common/UI/IfComponent";
import { TooltipPopupIcon } from "../../Common/UI/TooltipPopupIcon";
import { AdaptiveField } from "./FieldFactory";

// Utilities
import { Invisible } from "../../Utilities/EntryUtils";

// Signals
import { userData, userEmail } from "../../Common/Store/CommonSignals";
import {
  disabledSections,
  entryValues,
  readOnlyEntry,
  template,
} from "../../Entry/Manage/EditEntrySignals";

/**
 * TableField component for rendering and managing a table of dynamic fields.
 * @extends {Component}
 */
export class TableField extends Component {
  /**
   * Creates an instance of TableField.
   * @param {Object} props - The component props.
   * @param {Object} props.field - The field configuration object.
   * @param {string} props.field.fieldName - The name of the field.
   * @param {Object} props.field.properties - Additional properties for the field.
   * @param {number} props.sectionIndex - The index of the section containing this field.
   * @param {number} props.fieldIndex - The index of this field within its section.
   */
  constructor({
    field,
    sectionIndex,
    fieldIndex,
    isTabField,
    isStageField,
    isPrintPreview,
    handleSubmit,
  }) {
    super();
    const { fieldName, properties } = field;

    this.state = {
      values: [],
      invisible: false,
      disabled: false,
    };
    /**
     * The name of the field this table represents.
     * @type {string}
     */
    this.fieldName = fieldName;
    this.columns = properties?.Columns;
    this.label = properties?.Label || `${fieldName}:`;
    this.fieldInformation = properties?.FieldInformation;
    this.sectionIndex = sectionIndex;
    this.sectionName = template.value?.sections?.[sectionIndex]?.sectionName;
    this.fieldIndex = fieldIndex;
    this.isStageField = isStageField;
    this.isTabField = isTabField;
    this.isPrintPreview = isPrintPreview;
    this.handleSubmit = handleSubmit;
    this.visibilityRules = properties?.VisibilityRules;
    this.disposeDependencyEffect = () => {};
    this.disposeDisabledSectionsEffect = () => {};
  }

  //#region lifecycle functions
  /**
   * Lifecycle method called after the component mounts.
   * Initializes the component's state with values from entryValues.
   */
  componentDidMount() {
    this.setState({ values: entryValues.value[this.fieldName] || [] });
    if (this.visibilityRules) this.handleDependencyEffect();

    this.disposeDisabledSectionsEffect = effect(() => {
      const isSectionDisabled = disabledSections.value[this.sectionName];
      if (this.state.disabled !== isSectionDisabled) {
        this.setState({ disabled: isSectionDisabled });
      }
    });
  }

  /**
   * Lifecycle method called after the component updates.
   * Handles visibility changes and updates gallery values accordingly.
   */
  componentDidUpdate() {
    if (this.state.invisible && entryValues.value[this.fieldName] !== "") {
      this.setState(() => ({ values: [] }));
      entryValues.value = { ...entryValues.value, [this.fieldName]: "" };
    }
  }

  /**
   * Determines if the component should update.
   * @param {Object} _ - Unused nextProps parameter.
   * @param {Object} nextState - The next state of the component.
   * @returns {boolean} - Whether the component should update.
   */
  shouldComponentUpdate(_, nextState) {
    return (
      this.state.values !== nextState.values ||
      this.state.invisible !== nextState.invisible ||
      this.state.disabled !== nextState.disabled
    );
  }

  componentWillUnmount() {
    this.disposeDependencyEffect();
    this.disposeDisabledSectionsEffect();
  }

  handleDependencyEffect() {
    this.disposeDependencyEffect = effect(() => {
      const result = Invisible({
        VisibilityRules: this.visibilityRules,
        parameterValues: this.parameterValues,
        eventIndex: this.eventIndex,
        galleryName: this.galleryParentName,
      });

      this.setState({
        invisible: result.invisible && !result.disabled,
        disabled: result.invisible && result.disabled,
      });
    });
  }
  //#endregion lifecycle functions

  //#region custom functions
  /**
   * Calculates the next available index for a TableIndex column.
   * @param {Array} rows - Current rows in the table.
   * @returns {number} - The next available index.
   */
  getNextTableIndex = (rows) => {
    const tableIndexColumn = this.columns?.find((col) => col.columnType === "TableIndex");
    if (!tableIndexColumn) return 0;

    let maxIndex = 0;
    rows?.forEach((row) => {
      const indexValue = parseInt(row[tableIndexColumn.columnName] || "0", 10);
      if (!isNaN(indexValue) && indexValue > maxIndex) {
        maxIndex = indexValue;
      }
    });
    return maxIndex + 1;
  };

  /**
   * Adds a new row to the table.
   * @param {{ e: Event }} param0 - The object containing the event.
   * @param {Event} param0.e - The triggered event.
   */
  addNewRow = ({ e }) => {
    e.preventDefault();
    const newRow = { id: uuidv4() };

    this.columns?.forEach((column) => {
      if (column.columnType === "TableIndex") {
        // Set the pre-calculated index value
        const nextIndex = this.getNextTableIndex(entryValues.value[this.fieldName] || []);
        newRow[column.columnName] = nextIndex.toString();
      } else if (column.properties.DefaultValue) {
        if (column.columnType.includes("Date") || column.columnType === "Time") {
          const newDate = new Date();
          if (column.properties?.HoursOnly) {
            newDate.setMinutes(0);
            newDate.setSeconds(0);
            newDate.setMilliseconds(0);
          }
          const timezoneOffset = new Date().getTimezoneOffset() * 60000;
          const convertedDate = new Date(newDate.getTime() - timezoneOffset)
            .toISOString()
            .replace("Z", "");
          newRow[column.columnName] = convertedDate;
        } else if (column.columnType === "Checkbox") {
          newRow[column.columnName] = "false";
        } else if (column.columnType === "BusinessUnit") {
          newRow[column.columnName] = userData.value?.officeLocation;
        } else {
          newRow[column.columnName] = column.properties.DefaultValue;
        }
      } else {
        newRow[column.columnName] = "";
      }
    });

    const newEntryValues = JSON.tryParse(JSON.stringify(entryValues.value));
    newEntryValues[this.fieldName] = [...newEntryValues[this.fieldName], newRow];

    entryValues.value = newEntryValues;
    this.setState({ values: newEntryValues[this.fieldName] });
  };

  /**
   * Deletes a row from the table.
   * @param {{ e: Event, dataItem: Object }} param0 - The object containing the event and data item.
   * @param {Event} param0.e - The triggered event.
   * @param {Object} param0.dataItem - The data item to be deleted.
   */
  deleteRow = ({ e, dataItem }) => {
    e.preventDefault();
    const newEntryValues = JSON.tryParse(JSON.stringify(entryValues.value));
    const updatedRows = newEntryValues[this.fieldName]?.filter((item) => item.id !== dataItem.id);
    newEntryValues[this.fieldName] = updatedRows;
    entryValues.value = newEntryValues;
    this.setState({ values: updatedRows });
  };

  //#endregion custom functions

  //#region helper methods
  /**
   * Calculates the appropriate height for the Grid based on number of rows.
   * @returns {Object|undefined} - Style object with height or undefined for auto height.
   */
  getGridHeight = () => {
    const rowCount = this.state.values?.length || 0;

    // If more than 7 rows, use fixed height to enable virtual scrolling
    if (rowCount > 7) {
      return { height: "50vh" };
    }

    return undefined;
  };
  //#endregion helper methods

  //#region custom rendering
  /**
   * Uses ColorChangingIcon to render a delete button.
   * When clicked, it calls this.deleteRow with the event and dataItem.
   * @param {{dataItem: Object}} - The object containing the data item.
   * @param {Number} dataItem.id - The unique identifier for the row.
   * @returns {JSX.Element} - The rendered table cell.
   */
  DeleteActionButton = ({ dataItem }) => {
    return (
      <td className="k-vertical-align-top">
        <ColorChangingIcon
          icon={faTrashCan}
          initialPrimaryColor="#6e6e6e"
          initialSecondaryColor="#919191"
          padding="0 0 0 10px"
          onClick={(e) => this.deleteRow({ e, dataItem })}
        />
      </td>
    );
  };

  /**
   * Determines if approval mode should be enabled for the current field.
   * @param {Object} dataItem - The data item for the row.
   * @param {Object} field - The field configuration.
   * @returns {boolean} - Whether approval mode is enabled.
   */
  isApprovalModeEnabled(dataItem, field) {
    if (!readOnlyEntry.value) return false;

    const approvalModeFields = [
      "Pre Approval Status",
      "Final Approval Status",
      "Comments",
      "Status",
      "Commentary",
      "Send Notification",
    ];

    const approverNameFields = ["Department Approver", "Alternate Approver", "Approval Delegate"];

    let approverEmail = null;

    for (const dataField in dataItem) {
      if (approverNameFields.includes(dataField)) {
        const approverArray = JSON.tryParse(dataItem[dataField], []);
        if (approverArray.length > 0 && approverArray[0]?.email) {
          approverEmail = approverArray[0].email;
          break;
        }
      }
    }

    return (
      approverEmail?.toLowerCase() === userEmail.value.toLowerCase() &&
      approvalModeFields.includes(field.fieldName)
    );
  }

  /**
   * Retrieves a value from an object using a case-insensitive key lookup.
   * @param {Object} obj - The object to search within.
   * @param {string} fieldName - The field name to search for (case-insensitive).
   * @returns {*} The value associated with the field name, or undefined if not found.
   */
  getValueIgnoreCase = (obj, fieldName) => {
    const key = Object.keys(obj).find((k) => k.toLowerCase() === fieldName.toLowerCase());
    return key ? obj[key] : undefined;
  };

  /**
   * Renders an individual item in the table.
   * @param {{ e: Object, dataItem: Object, field: Object }} param0 - The object containing event, data item, and field information.
   * @param {Object} param0.e - The event object.
   * @param {Object} param0.dataItem - The data item for the row.
   * @param {Object} param0.field - The field configuration.
   * @returns {JSX.Element} - The rendered table cell.
   */
  ItemRender = ({ e, dataItem, field }) => {
    if (!dataItem) return;

    const column = this.columns?.find((c) => c.columnName === field.fieldName);
    const visibilityResult = Invisible({
      VisibilityRules: column?.properties?.VisibilityRules,
      parameterValues: column?.properties?.ParameterValues,
      eventIndex: e.dataIndex,
      galleryName: this.fieldName,
    });

    if (visibilityResult.invisible && !visibilityResult.disabled) {
      dataItem[field.fieldName] = "";
      return <td />;
    }

    /**
     * Handles changes to a field in the table.
     * @param {{ e: Event }} param0 - The object containing the event.
     * @param {Event} param0.e - The change event.
     */
    const handleFieldChange = ({ e }) => {
      // Allow empty string values, but prevent undefined
      if (e.target.value === undefined && e.value === "") return;

      // Split the name to get the field name and the event index
      // e.g. "field_0" => ["field", "0"]
      // e.g. "field_0_1" => ["field_0", "1"]

      const [fieldName, eventIndex] = e.target.name.split(/_(?=[^_]+$)/);

      const newTableValues = JSON.tryParse(JSON.stringify(entryValues.value));

      // Find the existing key with case-insensitive comparison
      const existingKey = Object.keys(newTableValues[this.fieldName][eventIndex]).find(
        (k) => k.toLowerCase() === fieldName.toLowerCase()
      );

      // Use the existing key if found, otherwise use the fieldName from event
      const keyToUse = existingKey || fieldName;

      newTableValues[this.fieldName][eventIndex][keyToUse] = e.value ?? ""; // Treat null as empty string
      entryValues.value = {
        ...entryValues.value,
        [this.fieldName]: newTableValues[this.fieldName],
      };
    };

    const approvalMode = this.isApprovalModeEnabled(dataItem, field);

    const props = {
      fieldName: field.fieldName,
      field: field,
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
      eventIndex: e.dataIndex,
      labelVisible: false,
      value: this.getValueIgnoreCase(dataItem, field.fieldName),
      galleryParentName: this.fieldName,
      isTabField: this.isTabField,
      isStageField: this.isStageField,
      isPrintPreview: this.isPrintPreview,
      approvalMode: readOnlyEntry.value && approvalMode,
      handleSubmit: this.handleSubmit,
      handleFieldChange: (data) => handleFieldChange({ ...data, galleryName: this.fieldName }),
    };

    return (
      <td className="k-vertical-align-top">
        <AdaptiveField
          fieldType={field.fieldType}
          {...props}
        />
      </td>
    );
  };
  //#endregion custom rendering

  /**
   * Renders the TableField component.
   * @returns {JSX.Element} - The rendered component.
   */
  render() {
    if (this.state.invisible) return;

    return (
      <div className="field-wrapper table-field">
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Label>{this.label}</Label>
          <If
            condition={
              this.fieldInformation !== undefined &&
              this.fieldInformation !== "" &&
              this.fieldInformation !== "<p></p>"
            }
          >
            <TooltipPopupIcon content={this.fieldInformation} />
          </If>
        </div>
        <Grid
          data-testid={this.fieldName}
          className={`${this.state.disabled ? "disabled-grid" : "new-entry-grid"} custom-grid`}
          data={this.state.values}
          editable={true}
          resizable={true}
          style={this.getGridHeight()}
        >
          {!this.isPrintPreview && !readOnlyEntry.value && (
            <GridToolbar>
              <Button onClick={(e) => this.addNewRow({ e })}>Add Row</Button>
            </GridToolbar>
          )}

          {this.columns?.map((column) => (
            <GridColumn
              key={column.columnName}
              field={column.columnName}
              title={column.properties?.Label || column.columnName}
              width={column.properties?.Width}
              cells={{
                data: (e) =>
                  this.ItemRender({
                    e,
                    dataItem: e.dataItem,
                    field: {
                      fieldName: column.columnName,
                      fieldType: column.columnType,
                      properties: column.properties,
                    },
                  }),
              }}
            />
          ))}
          {!this.isPrintPreview && !readOnlyEntry.value && (
            <GridColumn
              title="Actions"
              cells={{
                data: (e) => this.DeleteActionButton({ dataItem: e.dataItem }),
              }}
            />
          )}
        </Grid>
      </div>
    );
  }
}
